import { Browser } from '@capacitor/browser'
import { userStore } from '@/stores/user'
import { contractStore } from '@/stores/contract'
import { setCssVar } from 'quasar'
import { useI18n } from 'vue-i18n'

class ColorMapping {
  constructor () {
    this.colors = {
      light: 'white',
      tertiary: 'accent',
      'dark-blue': 'ultramarin',
      'light-blue': 'primary',
      'light-green': 'lime',
      'lighter-blue': 'primary-25',
      'light-sand': 'accent-25',
      '#ffffff': 'white'
    }
  }

  getColor (colorName, prefix = '', defaultColor = null) {
    if (!colorName && defaultColor) {
      colorName = defaultColor
    }
    colorName = colorName.replace('text-', '').replace('bg-', '').replace('btn-', '').toLowerCase()
    let className = this.colors[colorName] ?? colorName
    if (prefix && !className.startsWith(prefix)) {
      className = prefix + (prefix.endsWith('-') ? '' : '-') + className
    }
    return className
  }
}

export const colorMapping = new ColorMapping()

export function updateTheme () {
  const user = userStore()
  const contract = contractStore()
  if (user.partnerId && contract.isCampusflats) {
    setCssVar('primary', '#000099')
    // setCssVar('secondary', '#B90F5F')
    // setCssVar('accent', '#ddbd88')
  } else {
    setCssVar('primary', '#55afc8')
    // setCssVar('secondary', '#B90F5F')
    // setCssVar('accent', '#ddbd88')
  }
}

export const SvgIcons = {
  getSvgUse (name) {
    return 'svguse:' + import.meta.env.BASE_URL + 'icons.svg#' + name
  },
  get haus () {
    return this.getSvgUse('haus')
  },
  get vertragsdaten () {
    return this.getSvgUse('vertragsdaten')
  },
  get exchange () {
    return this.getSvgUse('exchange')
  },
  get kundendaten () {
    return this.getSvgUse('kundendaten')
  },
  get documents () {
    return this.getSvgUse('documents')
  },
  get euro () {
    return this.getSvgUse('euro')
  },
  get chart () {
    return this.getSvgUse('chart')
  },
  get wrench () {
    return this.getSvgUse('wrench')
  },
  get chat () {
    return this.getSvgUse('chat')
  },
  get prozentTuete () {
    return this.getSvgUse('prozent-tuete')
  },
  get lightbulb () {
    return this.getSvgUse('lightbulb')
  },
  get questionAnswer () {
    return this.getSvgUse('question-answer')
  },
  get stromGas () {
    return this.getSvgUse('strom-gas')
  },
  get fire () {
    return this.getSvgUse('fire')
  },
  get beiUns () {
    return this.getSvgUse('bei-uns')
  },
  get immobiliensuche () {
    return this.getSvgUse('immobiliensuche')
  },
  get off () {
    return this.getSvgUse('off')
  },
  get shield () {
    return this.getSvgUse('shield')
  },
  get profile () {
    return this.getSvgUse('profile')
  },
  get pages () {
    return this.getSvgUse('pages')
  },
  get pdf () {
    return this.getSvgUse('pdf')
  },
  get external () {
    return this.getSvgUse('external')
  },
  get laundry () {
    return this.getSvgUse('laundry')
  },
  get subscribe () {
    return this.getSvgUse('subscribe')
  },
  get contract () {
    return this.getSvgUse('contract')
  },
  get smarthome () {
    return this.getSvgUse('smarthome')
  },
  get blog () {
    return this.getSvgUse('blog')
  },
  get modernisierung () {
    return this.getSvgUse('modernisierung')
  },
  get faceId () {
    return this.getSvgUse('faceId')
  },
  get emergency () {
    return this.getSvgUse('emergency')
  },
  get kundenportal () {
    return this.getSvgUse('kundenportal')
  }
}

export function loadChatbot () {
  if (parseInt(import.meta.env.QCLI_CHATBOT_ENABLED) === 1 && !loadChatbot.loaded) {
    loadChatbot.loaded = true
    const chatHost = import.meta.env.QCLI_CHATBOT_BASE_URL
    const configurationId = import.meta.env.QCLI_CHATBOT_CONFIGURATION_ID
    const script = document.createElement('script')
    script.onload = () => {
      // eslint-disable-next-line no-undef
      bootstrapChat(chatHost, chatHost, configurationId, 'kundenportal no-chatbot-button')
    }
    script.src = chatHost + 'bootstrapChat.js'
    document.head.appendChild(script)
  }
}

export function germanDateToObject (value) {
  if (!value || value.search(/^[0-9]{1,2}\.[0-9]{1,2}\.[0-9]{1,4}$/) < 0) {
    return null
  }
  return new Date(value.split('.').reverse().join('/') + ' 12:00:00')
}

export function triggerEtEvent (type, value) {
  // eslint-disable-next-line camelcase
  if (typeof ET_Event === 'object') {
    if (type === 'download') {
      ET_Event.download(value) // eslint-disable-line
    } else if (type === 'click') {
      ET_Event.click(value) // eslint-disable-line
    } else {
      _etracker.sendEvent(new et_UserDefinedEvent(value, type)) // eslint-disable-line
    }
  }
  if (typeof _paq === 'object') {
    _paq.push(['trackEvent', type, value]) // eslint-disable-line
  }
}

export function getRandomInt (min = 0, max = 100) {
  const minCeiled = Math.ceil(min)
  const maxFloored = Math.floor(max)
  return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled)
}

export function formatDateObject (date, format = 'simple', replaceTodayYesterday = true) {
  const { d } = useI18n()
  if (date === null || date === undefined || date === '') {
    return null
  }
  if (typeof date === 'string') {
    try {
      if (date.includes('.')) {
        date = date.split('.')
        if (date.length !== 3) {
          throw new Error('Invalid date format')
        }
        date = date[2] + '-' + date[1] + '-' + date[0]
      }
      if (date === '0000-00-00' || date === '9999-12-31') {
        return '-'
      }
      date = new Date(date)
    } catch (err) {
      console.error(err)
      return null
    }
  }
  if (replaceTodayYesterday) {
    const currentDate = new Date()
    if (currentDate.getDate() === date.getDate() && currentDate.getMonth() === date.getMonth() && currentDate.getFullYear() === date.getFullYear()) {
      return 'Heute'
    }
    currentDate.setDate(currentDate.getDate() - 1)
    if (currentDate.getDate() === date.getDate() && currentDate.getMonth() === date.getMonth() && currentDate.getFullYear() === date.getFullYear()) {
      return 'Gestern'
    }
  }
  return d(date, format)
}

export function openExternal (event, url) {
  if (window.deviceInfo.platform !== 'web') {
    event.preventDefault()
    Browser.open({ url })
  }
}

function getEaster (Y) {
  const C = Math.floor(Y / 100)
  const N = Y - 19 * Math.floor(Y / 19)
  const K = Math.floor((C - 17) / 25)
  let I = C - Math.floor(C / 4) - Math.floor((C - K) / 3) + 19 * N + 15
  I = I - 30 * Math.floor((I / 30))
  I = I - Math.floor(I / 28) * (1 - Math.floor(I / 28) * Math.floor(29 / (I + 1)) * Math.floor((21 - N) / 11))
  let J = Y + Math.floor(Y / 4) + I + 2 - C + Math.floor(C / 4)
  J = J - 7 * Math.floor(J / 7)
  const L = I - J
  const M = 3 + Math.floor((L + 40) / 44)
  const D = L + 28 - 31 * Math.floor(M / 4)
  return Y + '-' + M + '-' + D
}

export function isHoliday (date = null) {
  if (!(date instanceof Date)) {
    date = new Date(date)
  }
  const dayMonth = date.getDate() + '.' + (date.getMonth() + 1)
  const holidays = []
  holidays.push('1.1', '1.5', '3.10', '1.11', '24.12', '25.12', '26.12', '31.12')
  const easter = new Date(getEaster(date.getFullYear()))
  holidays.push(easter.getDate() + '.' + (easter.getMonth() + 1)) // Ostersonntag
  easter.setDate(easter.getDate() - 2)
  holidays.push(easter.getDate() + '.' + (easter.getMonth() + 1)) // Karfreitag
  easter.setDate(easter.getDate() + 3)
  holidays.push(easter.getDate() + '.' + (easter.getMonth() + 1)) // Ostermontag
  easter.setDate(easter.getDate() + 38)
  holidays.push(easter.getDate() + '.' + (easter.getMonth() + 1)) // Christi Himmelfahrt
  easter.setDate(easter.getDate() + 10)
  holidays.push(easter.getDate() + '.' + (easter.getMonth() + 1)) // Pfingssonntag
  easter.setDate(easter.getDate() + 1)
  holidays.push(easter.getDate() + '.' + (easter.getMonth() + 1)) // Pfingstmontag
  easter.setDate(easter.getDate() + 10)
  holidays.push(easter.getDate() + '.' + (easter.getMonth() + 1)) // Fronleichnam
  return holidays.includes(dayMonth)
}

export function trimChar (str, ch) {
  let start = 0
  let end = str.length
  while (start < end && str[start] === ch) {
    ++start
  }
  while (end > start && str[end - 1] === ch) {
    --end
  }
  return (start > 0 || end < str.length) ? str.substring(start, end) : str
}

export function hasAccess (id, contract = null, router = null) {
  if (!contract) {
    contract = contractStore()
  }
  if (typeof id === 'number') {
    id = id.toString()
  }
  if (typeof contract.sites[id] === 'undefined') {
    if (router) {
      router.push({ name: 'Dashboard' })
    }
    return false
  }
  return true
}
